/**
 * The diff renderer — docs/08-frontend-and-ux.md §7.1.
 *
 * One component switching on `proposal_kind`, fed by the api's pre-built
 * `diff: DiffRow[]` (docs/05 §9.2): `add_*` arrives as every populated field
 * with `from: null` and renders as additions; `edit_*` arrives as the stored
 * `change_set` and renders `from → to`; `retire_*` arrives as the status flip
 * and its reason.
 *
 * Field *names* are rendered through i18n with a raw fallback, which is what
 * §7.1 means by "schema-driven": a column added by a migration shows up here
 * (raw but correct) without a frontend release.
 */

import { useTranslation } from 'react-i18next';
import type { DiffRow, ProposalKind } from '@famtree/shared';
import { FuzzyDateText } from '../../ui/FuzzyDateField.js';
import styles from './proposals.module.css';
import ui from '../../ui/components.module.css';

const FUZZY_FIELDS = new Set(['birth', 'death', 'started', 'ended']);

export function ProposalDiff({ kind, diff }: { kind: ProposalKind; diff: DiffRow[] }): JSX.Element {
  const { t } = useTranslation();
  const isAddition = kind === 'add_person' || kind === 'add_relation';

  if (diff.length === 0) return <p className={ui.muted}>{t('proposals.noDiff')}</p>;

  return (
    <table className={styles.diff}>
      <thead>
        <tr>
          <th scope="col">{t('proposals.diff.field')}</th>
          {!isAddition && <th scope="col">{t('proposals.diff.from')}</th>}
          <th scope="col">{isAddition ? t('proposals.diff.value') : t('proposals.diff.to')}</th>
        </tr>
      </thead>
      <tbody>
        {diff.map((row) => (
          <tr key={row.field} className={isAddition ? styles.diffAdded : undefined}>
            <th scope="row" className={styles.diffField}>
              {fieldLabel(row.field, t)}
            </th>
            {!isAddition && (
              <td className={styles.diffFrom}>
                <DiffValue field={row.field} value={row.from} />
              </td>
            )}
            <td>
              <DiffValue field={row.field} value={row.to} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** A translated field name where we have one, the raw column name where we do not. */
function fieldLabel(field: string, t: (key: string) => string): string {
  const key = `proposals.fields.${field}`;
  const translated = t(key);
  return translated === key ? field : translated;
}

function DiffValue({ field, value }: { field: string; value: unknown }): JSX.Element {
  const { t } = useTranslation();

  if (value == null || value === '') {
    return <span className={styles.removed}>{t('proposals.diff.removed')}</span>;
  }
  // Fuzzy dates are one row, rendered in their display form (§7.1), never as the
  // three columns they are stored in.
  if (FUZZY_FIELDS.has(field) && typeof value === 'object') {
    return <FuzzyDateText value={value as never} />;
  }
  if (typeof value === 'boolean') return <>{value ? t('app.yes') : t('app.no')}</>;
  if (typeof value === 'object') return <code>{JSON.stringify(value)}</code>;

  // Enum-valued columns read as vocabulary, not as identifiers, where a key exists.
  const key = `enums.${enumNamespace(field)}.${String(value)}`;
  const translated = t(key);
  return <>{translated === key ? String(value) : translated}</>;
}

/** Maps a column to the enum vocabulary it draws from (docs/02 §2). */
function enumNamespace(field: string): string {
  switch (field) {
    case 'gender':
      return 'gender';
    case 'living_status':
      return 'living_status';
    case 'status':
      return 'assertion_status';
    case 'kind':
      return 'relation_kind';
    case 'parent_role':
      return 'parent_role';
    case 'parent_basis':
      return 'parent_basis';
    case 'union_type':
      return 'union_type';
    case 'end_reason':
      return 'union_end_reason';
    case 'sibling_basis':
      return 'sibling_basis';
    case 'retire_reason':
      return 'retire_reason';
    default:
      return '_none';
  }
}
